<script>
  import { FEEDBACK_EMOJI, FEEDBACK_LABEL, SOURCE_LABEL, WHY_LABEL } from '../lib/feedback.js';
  import { api, apiDownload } from '../lib/api.js';

  export let ecardId;
  export let viewCount = 0;
  export let startOpen = false;   // render expanded immediately (list page drives this)
  export let hideToggle = false;  // hide the built-in toggle (page owns the trigger)

  let open = startOpen;
  let loaded = false;
  let loading = false;
  let error = null;
  let views = [];
  let distinctDevices = 0;
  let truncated = false;
  let recipientViewedAt = null;
  let purchaserViewedAt = null;
  let thanksInviteSeenAt = null;
  let thanksComposeOpenedAt = null;
  let thanksRepliedAt = null;
  let thanksCodeEmailedAt = null;
  let thanksNewsletter = false;
  let thanksNewsletterConfirmedAt = null;
  let openedNoticeAt = null;
  let openedNoticeFeedback = false;
  let feedback = null;
  // Story cards (flip book): page count from the product, deepest page the
  // recipient reached (N+1 = message page), device they opened on, and lead-form
  // submissions on a brand story card. See planning/cardhero-ecard-flip-story-book.md.
  let storyPageMax = 0;
  let storyPageCount = 0;
  let leadCount = 0;
  let lastLeadAt = null;
  let recipientViewport = null;
  let leads = [];
  let leadsError = null;
  let downloading = null;
  // Campaign links (press / outreach): opaque /e/{code} share links with their
  // click + view counts. See planning/cardhero-ecard-campaign-links.md.
  let links = [];
  let newCampaign = '';
  let newVia = '';
  let minting = false;
  let linkError = null;
  let copied = null;

  // Lazy-load the first time the panel is opened.
  $: if (open && !loaded && !loading) load();

  async function load() {
    loading = true;
    error = null;
    try {
      const d = await api(`/ecards/${ecardId}/views`);
      views = d.views || [];
      distinctDevices = d.distinctDevices || 0;
      truncated = !!d.truncated;
      recipientViewedAt = d.recipientViewedAt || null;
      purchaserViewedAt = d.purchaserViewedAt || null;
      thanksInviteSeenAt = d.thanksInviteSeenAt || null;
      thanksComposeOpenedAt = d.thanksComposeOpenedAt || null;
      thanksRepliedAt = d.thanksRepliedAt || null;
      thanksCodeEmailedAt = d.thanksCodeEmailedAt || null;
      thanksNewsletter = !!d.thanksNewsletter;
      thanksNewsletterConfirmedAt = d.thanksNewsletterConfirmedAt || null;
      openedNoticeAt = d.openedNoticeAt || null;
      openedNoticeFeedback = !!d.openedNoticeFeedback;
      feedback = d.feedback || null;
      storyPageMax = d.storyPageMax || 0;
      storyPageCount = d.storyPageCount || 0;
      leadCount = d.leadCount || 0;
      lastLeadAt = d.lastLeadAt || null;
      recipientViewport = d.recipientViewport || null;
      try {
        const l = await api(`/ecards/${ecardId}/links`);
        links = l.links || [];
        if (!newCampaign && links.length) newCampaign = links[links.length - 1].campaign;
      } catch (e) {
        linkError = e.message;
      }
      if (leadCount) {
        try {
          leads = (await api(`/ecards/${ecardId}/leads`)).leads || [];
        } catch (e) {
          leadsError = e.message;
        }
      }
      loaded = true;
    } catch (e) {
      error = e.message;
    } finally {
      loading = false;
    }
  }

  async function mintLink() {
    linkError = null;
    minting = true;
    try {
      const d = await api(`/ecards/${ecardId}/links`, { method: 'POST', body: JSON.stringify({ campaign: newCampaign, via: newVia }) });
      links = [...links, d.link];
      newVia = '';
    } catch (e) {
      linkError = e.message;
    } finally {
      minting = false;
    }
  }

  async function copyLink(l) {
    try {
      await navigator.clipboard.writeText(l.url);
      copied = l.code;
      setTimeout(() => { if (copied === l.code) copied = null; }, 1500);
    } catch (e) { /* clipboard blocked — the URL is visible to select */ }
  }

  async function download(what) {
    downloading = what;
    try {
      await apiDownload(`/ecards/${ecardId}/${what}.csv`, `ecard-${ecardId.slice(0, 8)}-${what}.csv`);
    } catch (e) {
      if (what === 'leads') leadsError = e.message; else linkError = e.message;
    } finally {
      downloading = null;
    }
  }

  // Page depth label: 1..N = a story page, N+1 = the message page.
  function depth(pageMax) {
    if (!pageMax) return '—';
    if (storyPageCount && pageMax > storyPageCount) return 'message';
    return `page ${pageMax}${storyPageCount ? ' / ' + storyPageCount : ''}`;
  }
  const DEVICE_ICON = { phone: '📱', tablet: '📟', desktop: '💻' };
  function deviceText(vp) {
    if (!vp) return null;
    return `${DEVICE_ICON[vp.class] || ''} ${vp.class}${vp.w && vp.h ? ` ${vp.w}×${vp.h}` : ''}`.trim();
  }

  // Per-campaign rollup of the links table (one brand send = one campaign,
  // one link per recipient): links sent, read, deepest-page distribution, leads.
  $: campaigns = (() => {
    const by = new Map();
    for (const l of links) {
      const c = by.get(l.campaign) || { campaign: l.campaign, links: 0, clicks: 0, views: 0, read: 0, message: 0, depthSum: 0, depthN: 0, leads: 0 };
      c.links += 1; c.clicks += l.clicks; c.views += l.views; c.leads += l.leads || 0;
      if (l.views > 0) c.read += 1;
      if (l.pageMax) { c.depthN += 1; c.depthSum += l.pageMax; if (storyPageCount && l.pageMax > storyPageCount) c.message += 1; }
      by.set(l.campaign, c);
    }
    return [...by.values()].map((c) => ({ ...c, avgDepth: c.depthN ? Math.round((10 * c.depthSum) / c.depthN) / 10 : null }));
  })();

  // Loads by device class (viewport capture live from 2026-10-16; earlier rows unknown).
  $: deviceSplit = (() => {
    const n = { phone: 0, tablet: 0, desktop: 0 };
    let known = 0;
    for (const v of views) if (!v.stage && v.viewport && n[v.viewport.class] !== undefined) { n[v.viewport.class] += 1; known += 1; }
    return { ...n, known };
  })();

  function fmt(s) {
    if (!s) return '—';
    try {
      // ISO/UTC from the server → staff's local time (UK = BST/GMT).
      return new Date(s).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'medium' });
    } catch (e) {
      return s;
    }
  }

  const ROLE_LABEL = { recipient: 'recipient', purchaser: 'purchaser', 'purchaser-thanks': 'purchaser · thank-you', admin: 'staff', organic: 'unattributed', campaign: 'campaign' };
  const roleLabel = (r) => ROLE_LABEL[r] || 'legacy';
  const STAGE_LABEL = { back: 'turned to back cover', compose: 'opened reply form', page: 'reached page' };
  const stageLabel = (v) => v.stage === 'page' ? (storyPageCount && v.page > storyPageCount ? 'reached the message page' : `reached page ${v.page}`) : (STAGE_LABEL[v.stage] || v.stage);
  const RATING_LABEL = Object.fromEntries(Object.keys(FEEDBACK_LABEL).map((k) => [k, `${FEEDBACK_EMOJI[k]} ${FEEDBACK_LABEL[k]}`]));
</script>

{#if !hideToggle}
  <button class="toggle" on:click={() => (open = !open)}>
    {open ? '▾' : '▸'} access log{#if viewCount} ({viewCount}){/if}
  </button>
{/if}

{#if open}
  <div class="log">
    {#if loading}
      <p class="muted">Loading…</p>
    {:else if error}
      <p class="err">⚠️ {error}</p>
    {:else}
      <div class="summary" class:opened={recipientViewedAt}>
        {#if recipientViewedAt}
          ✓ Recipient opened — {fmt(recipientViewedAt)}{#if recipientViewport} <span class="device" title="Viewport the recipient first opened on (width × height in CSS px)">{deviceText(recipientViewport)}</span>{/if}
        {:else}
          ○ Recipient has not opened it yet
        {/if}
      </div>
      {#if purchaserViewedAt}
        <div class="sub">Purchaser previewed — {fmt(purchaserViewedAt)}</div>
      {/if}
      {#if storyPageCount}
        <div class="sub story">
          📖 Story card · {storyPageCount} page{storyPageCount === 1 ? '' : 's'} + message ·
          {#if storyPageMax}
            recipient reached <strong>{depth(storyPageMax)}</strong>
            <span class="depth-bar" title="Pages the recipient turned through">
              {#each Array(storyPageCount + 1) as _, i}<i class:hit={i < storyPageMax} class:msg={i === storyPageCount}></i>{/each}
            </span>
          {:else if recipientViewedAt}
            recipient opened the cover but turned no page
          {:else}
            recipient depth shows here once they open it (campaign viewers are counted per link below)
          {/if}
        </div>
      {/if}
      {#if recipientViewedAt && (thanksInviteSeenAt || thanksComposeOpenedAt || thanksRepliedAt)}
        <div class="sub funnel">
          Say-thanks funnel:
          <span class="step" class:hit={thanksInviteSeenAt}>{thanksInviteSeenAt ? '✓' : '○'} saw invite{#if thanksInviteSeenAt} {fmt(thanksInviteSeenAt)}{/if}</span> →
          <span class="step" class:hit={thanksComposeOpenedAt}>{thanksComposeOpenedAt ? '✓' : '○'} opened form{#if thanksComposeOpenedAt} {fmt(thanksComposeOpenedAt)}{/if}</span> →
          <span class="step" class:hit={thanksRepliedAt}>{thanksRepliedAt ? '✓' : '○'} replied</span>
        </div>
      {:else if recipientViewedAt}
        <div class="sub">Say-thanks funnel: opened the card but has not turned to the back cover (marker live from 2026-09-14 — earlier cards show nothing here)</div>
      {/if}
      {#if thanksRepliedAt}
        <div class="sub">💌 Recipient replied — {fmt(thanksRepliedAt)}{#if thanksCodeEmailedAt} · 🎟️ code emailed {fmt(thanksCodeEmailedAt)}{/if}{#if thanksNewsletterConfirmedAt} · 📣 newsletter confirmed {fmt(thanksNewsletterConfirmedAt)}{:else if thanksNewsletter} · 📣 newsletter pending confirmation{/if}</div>
      {/if}

      {#if openedNoticeAt}
        <div class="sub">📬 Sender told it was opened — {fmt(openedNoticeAt)}{#if openedNoticeFeedback} · asked “how did we do?”{/if}</div>
      {/if}
      {#if feedback}
        <div class="feedback">
          <div class="fb-head">
            Sender feedback{#if feedback.rating}: <strong>{RATING_LABEL[feedback.rating] || feedback.rating}</strong>{/if}
            {#if feedback.submittedAt}<span class="fb-meta">sent {fmt(feedback.submittedAt)}</span>{:else}<span class="fb-meta">rating only (form not sent)</span>{/if}
            {#if feedback.trustpilotClickedAt}<span class="fb-tp">★ clicked Trustpilot</span>{/if}
          </div>
          {#if feedback.source}<div>Found us: {SOURCE_LABEL[feedback.source] || feedback.source}</div>{/if}
          {#if feedback.why.length}<div>Why an ecard: {feedback.why.map((w) => WHY_LABEL[w] || w).join(', ')}</div>{/if}
          {#if feedback.again}<div>Send another: {feedback.again}</div>{/if}
          {#if feedback.fiddly}<div class="fb-text"><em>Fiddly:</em> {feedback.fiddly}</div>{/if}
          {#if feedback.wish}<div class="fb-text"><em>Wished for:</em> {feedback.wish}</div>{/if}
        </div>
      {/if}

      {#if views.length === 0}
        <p class="muted">No views recorded yet.</p>
      {:else}
        <ol>
          {#each views as v}
            <li>
              <span class="ts">{fmt(v.timestamp)}</span>
              <span class="meta">
                {#if v.role}<span class="role {v.role}">{roleLabel(v.role)}{#if v.role === 'campaign' && (v.via || v.campaign)} · {v.via || v.campaign}{/if}</span>{/if}
                {#if v.stage}<span class="stage">{stageLabel(v)}</span>{/if}
                {#if v.viewport}<span class="dev" title="Viewport {v.viewport.w}×{v.viewport.h}">{DEVICE_ICON[v.viewport.class] || ''} {v.viewport.class}</span>{/if}
                {#if v.device}<span class="dev">device {v.device}</span>{/if}
              </span>
            </li>
          {/each}
        </ol>
        <p class="foot">
          {views.length} load{views.length === 1 ? '' : 's'} ·
          ~{distinctDevices} source{distinctDevices === 1 ? '' : 's'}{#if truncated} · latest {views.length} shown{/if}
          {#if deviceSplit.known} · <span title="Page loads by viewport class (recorded from 16 Oct 2026; stage pings and older loads not counted)">{#if deviceSplit.phone}📱 {deviceSplit.phone} phone {/if}{#if deviceSplit.tablet}📟 {deviceSplit.tablet} tablet {/if}{#if deviceSplit.desktop}💻 {deviceSplit.desktop} desktop{/if}</span>{/if}
        </p>
      {/if}
      <p class="note">“recipient” = opened via the unique link only they were sent (confident). “purchaser”/“staff” = sender or your own previews. “campaign” = opened via one of the campaign links below. “unattributed” = a load with no token (e.g. a forwarded link); “legacy” = before view-token tracking.</p>

      <div class="links">
        <div class="links-head">Campaign links <span class="fb-meta">press / outreach — each link looks like an ordinary ecard link; the label stays here</span></div>
        {#if links.length}
          <div class="rollup">
            {#each campaigns as c (c.campaign)}
              <div class="camp" title="One row per campaign label. Read = links with at least one human view. Depth = deepest page reached per link, averaged over links that turned a page (message page counts as {storyPageCount ? storyPageCount + 1 : 'N+1'}).">
                <strong>{c.campaign}</strong> · {c.links} link{c.links === 1 ? '' : 's'} · {c.clicks} click{c.clicks === 1 ? '' : 's'} · {c.views} view{c.views === 1 ? '' : 's'} · <span class:hit={c.read}>{c.read} read</span>
                {#if storyPageCount} · <span class:hit={c.message}>{c.message} reached the message</span>{#if c.avgDepth} · avg depth {c.avgDepth}{/if}{/if}
                {#if c.leads} · <span class="lead-n">🧲 {c.leads} lead{c.leads === 1 ? '' : 's'}</span>{/if}
              </div>
            {/each}
            <button class="csv" on:click={() => download('links')} disabled={downloading === 'links'}>{downloading === 'links' ? 'Preparing…' : '⬇ Links CSV'}</button>
          </div>
          <table>
            <thead><tr><th>Via</th><th>Campaign</th><th>Link</th><th class="n">Clicks</th><th class="n">Views</th>{#if storyPageCount}<th>Deepest page</th>{/if}<th class="n">Leads</th><th>First viewed</th><th>Last viewed</th></tr></thead>
            <tbody>
              {#each links as l}
                <tr class:read={l.views > 0}>
                  <td><strong>{l.via || '—'}</strong></td>
                  <td>{l.campaign}</td>
                  <td class="url"><code>{l.url}</code> <button class="copy" on:click={() => copyLink(l)}>{copied === l.code ? 'copied' : 'copy'}</button></td>
                  <td class="n">{l.clicks}</td>
                  <td class="n">{l.views}</td>
                  {#if storyPageCount}<td class="depth" class:msg={l.pageMax > storyPageCount}>{depth(l.pageMax)}</td>{/if}
                  <td class="n" class:lead-n={l.leads}>{l.leads || '—'}</td>
                  <td>{l.firstViewAt ? fmt(l.firstViewAt) : '—'}</td>
                  <td>{l.lastViewAt ? fmt(l.lastViewAt) : '—'}</td>
                </tr>
              {/each}
            </tbody>
          </table>
          <p class="note">Clicks count every hit on the link, including mail-system link scanners (Outlook SafeLinks etc.), so a click seconds after sending is “delivered”. Views need the page’s JavaScript to run, so a view is a person.{#if storyPageCount} Deepest page is the furthest any viewer of that link got; “message” = they turned past every story page.{/if}</p>
        {/if}
        <form class="mint" on:submit|preventDefault={mintLink}>
          <input placeholder="campaign (e.g. press-2026-10)" bind:value={newCampaign} pattern="[a-z0-9][a-z0-9-]{'{'}0,39{'}'}" required />
          <input placeholder="via (e.g. guardian)" bind:value={newVia} pattern="[a-z0-9][a-z0-9-]{'{'}0,39{'}'}" required />
          <button type="submit" disabled={minting}>{minting ? 'Minting…' : 'Mint link'}</button>
        </form>
        {#if linkError}<p class="err">⚠️ {linkError}</p>{/if}
      </div>

      {#if leadCount || leads.length}
        <div class="leads">
          <div class="links-head">🧲 Leads <span class="fb-meta">{leadCount} submission{leadCount === 1 ? '' : 's'} from the card's lead form{#if lastLeadAt} · latest {fmt(lastLeadAt)}{/if}</span>
            <button class="csv" on:click={() => download('leads')} disabled={downloading === 'leads'}>{downloading === 'leads' ? 'Preparing…' : '⬇ Leads CSV'}</button>
          </div>
          {#if leadsError}<p class="err">⚠️ {leadsError}</p>{/if}
          {#if leads.length}
            <table>
              <thead><tr><th>When</th><th>Via</th><th>Name</th><th>Email</th><th>Company</th><th>Message</th></tr></thead>
              <tbody>
                {#each leads as l (l.id)}
                  <tr>
                    <td class="ts">{fmt(l.sentAt)}</td>
                    <td>{#if l.via}<strong>{l.via}</strong>{#if l.campaign} <span class="fb-meta">{l.campaign}</span>{/if}{:else}<span class="fb-meta">{roleLabel(l.role)}</span>{/if}</td>
                    <td>{l.name || '—'}</td>
                    <td><a href="mailto:{l.email}">{l.email}</a></td>
                    <td>{l.company || '—'}</td>
                    <td class="msg">{l.message || '—'}</td>
                  </tr>
                {/each}
              </tbody>
            </table>
          {/if}
        </div>
      {/if}
    {/if}
  </div>
{/if}

<style>
  .toggle {
    cursor: pointer; border: 0; background: none; padding: 0; margin: 0;
    color: #6b21a8; font-size: .82rem; font-weight: 600;
  }
  .toggle:hover { text-decoration: underline; }
  .log {
    margin-top: .4rem; border: 1px solid #e9d5ff; border-radius: 6px;
    background: #fff; padding: .5rem .6rem;
  }
  .summary { font-size: .85rem; font-weight: 700; color: #92400e; padding: .1rem 0 .35rem; }
  .summary.opened { color: #166534; }
  .sub { font-size: .76rem; color: #777; margin: -.2rem 0 .35rem; }
  ol { margin: 0; padding: 0; list-style: none; }
  li {
    display: flex; justify-content: space-between; gap: .75rem;
    padding: .25rem 0; border-bottom: 1px solid #f3f0f8; font-size: .82rem;
  }
  li:last-child { border-bottom: 0; }
  .ts { font-variant-numeric: tabular-nums; color: #222; }
  .meta { display: flex; gap: .4rem; align-items: center; white-space: nowrap; }
  .role { font-size: .68rem; font-weight: 700; padding: .05rem .4rem; border-radius: 999px; text-transform: uppercase; letter-spacing: .02em; }
  .role.recipient { background: #dcfce7; color: #166534; }
  .role.purchaser { background: #dbeafe; color: #1e40af; }
  .role.purchaser-thanks { background: #fce7f3; color: #9d174d; } /* sender returning via the reply email */
  .role.admin { background: #e5e7eb; color: #374151; }
  .role.organic { background: #f3e8ff; color: #6b21a8; }
  .role.campaign { background: #fef3c7; color: #92400e; text-transform: none; }
  .links { margin-top: .6rem; border-top: 1px dashed #e9d5ff; padding-top: .45rem; }
  .links-head { font-size: .8rem; font-weight: 700; color: #92400e; margin-bottom: .3rem; }
  .links table { width: 100%; border-collapse: collapse; font-size: .78rem; }
  .links th { text-align: left; font-weight: 600; color: #777; padding: .15rem .35rem; border-bottom: 1px solid #eee; }
  .links td { padding: .2rem .35rem; border-bottom: 1px solid #f3f0f8; vertical-align: top; }
  .links .n { text-align: right; font-variant-numeric: tabular-nums; }
  .links tr.read td { background: #f0fdf4; }
  .links .url code { font-size: .74rem; }
  .links .copy { font-size: .68rem; border: 1px solid #ddd; background: #fff; border-radius: 4px; padding: 0 .35rem; cursor: pointer; margin-left: .25rem; }
  .mint { display: flex; gap: .35rem; margin-top: .4rem; flex-wrap: wrap; }
  .mint input { font-size: .78rem; padding: .25rem .4rem; border: 1px solid #ddd; border-radius: 4px; min-width: 12rem; }
  .mint button { font-size: .78rem; padding: .25rem .6rem; border: 0; border-radius: 4px; background: #92400e; color: #fff; cursor: pointer; }
  .mint button:disabled { opacity: .6; cursor: default; }
  .dev { color: #888; font-size: .76rem; }
  .device { font-weight: 500; color: #555; font-size: .76rem; margin-left: .3rem; }
  .story { color: #1e3a8a; }
  .story strong { color: #1e3a8a; }
  .depth-bar { display: inline-flex; gap: 2px; vertical-align: middle; margin-left: .3rem; }
  .depth-bar i { display: inline-block; width: 9px; height: 9px; border-radius: 2px; background: #e5e7eb; }
  .depth-bar i.hit { background: #3b82f6; }
  .depth-bar i.msg { border-radius: 50%; }
  .depth-bar i.msg.hit { background: #166534; }
  .rollup { display: flex; flex-wrap: wrap; align-items: center; gap: .3rem .8rem; margin: 0 0 .4rem; font-size: .78rem; color: #444; }
  .camp .hit { color: #166534; font-weight: 600; }
  .lead-n { color: #9d174d; font-weight: 600; }
  .links td.depth { color: #1e40af; }
  .links td.depth.msg { color: #166534; font-weight: 600; }
  .csv { font-size: .7rem; border: 1px solid #ddd; background: #fff; border-radius: 4px; padding: .1rem .45rem; cursor: pointer; color: #444; margin-left: auto; }
  .csv:disabled { opacity: .6; cursor: default; }
  .leads { margin-top: .6rem; border-top: 1px dashed #e9d5ff; padding-top: .45rem; }
  .leads .links-head { display: flex; align-items: center; gap: .4rem; }
  .leads table { width: 100%; border-collapse: collapse; font-size: .78rem; }
  .leads th { text-align: left; font-weight: 600; color: #777; padding: .15rem .35rem; border-bottom: 1px solid #eee; }
  .leads td { padding: .2rem .35rem; border-bottom: 1px solid #f3f0f8; vertical-align: top; }
  .leads td.msg { white-space: pre-wrap; max-width: 28rem; }
  .stage { font-size: .7rem; color: #9d174d; font-style: italic; }
  .funnel .step { color: #999; }
  .funnel .step.hit { color: #166534; font-weight: 600; }
  .foot { margin: .45rem 0 0; font-size: .78rem; color: #555; font-weight: 600; }
  .note { margin: .25rem 0 0; font-size: .72rem; color: #999; line-height: 1.3; }
  .feedback { font-size: .8rem; color: #333; background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 6px; padding: .4rem .55rem; margin: 0 0 .5rem; line-height: 1.45; }
  .fb-head { font-weight: 600; margin-bottom: .15rem; }
  .fb-meta { font-weight: 400; color: #777; font-size: .74rem; margin-left: .4rem; }
  .fb-tp { font-weight: 600; color: #00896b; font-size: .74rem; margin-left: .4rem; }
  .fb-text { white-space: pre-wrap; }
  .muted { color: #777; font-size: .82rem; margin: .2rem 0; }
  .err { color: #b00020; font-size: .82rem; margin: .2rem 0; }
</style>
